import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const DisplayPreferences = () => {
  const [displaySettings, setDisplaySettings] = useState({
    currency: 'USD',
    timezone: 'America/New_York',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    chartTheme: 'dark',
    dashboardLayout: 'grid',
    showGridLines: true,
    showVolume: true,
    showIndicators: true,
    autoRefresh: true,
    refreshInterval: 5,
    compactMode: false,
    highContrast: false,
    fontSize: 'medium',
    chartAnimations: true,
    soundAlerts: true,
    desktopNotifications: true
  });

  const currencyOptions = [
    { value: 'USD', label: 'US Dollar (USD)' },
    { value: 'EUR', label: 'Euro (EUR)' },
    { value: 'GBP', label: 'British Pound (GBP)' },
    { value: 'JPY', label: 'Japanese Yen (JPY)' },
    { value: 'BTC', label: 'Bitcoin (BTC)' },
    { value: 'ETH', label: 'Ethereum (ETH)' }
  ];

  const timezoneOptions = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
    { value: 'UTC', label: 'UTC' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (US)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (EU)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (ISO)' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY' }
  ];

  const timeFormatOptions = [
    { value: '12h', label: '12 Hour (AM/PM)' },
    { value: '24h', label: '24 Hour' }
  ];

  const chartThemeOptions = [
    { value: 'dark', label: 'Dark Theme' },
    { value: 'light', label: 'Light Theme' },
    { value: 'auto', label: 'Auto (System)' },
    { value: 'high-contrast', label: 'High Contrast' }
  ];

  const layoutOptions = [
    { value: 'grid', label: 'Grid Layout' },
    { value: 'list', label: 'List Layout' },
    { value: 'compact', label: 'Compact Layout' },
    { value: 'custom', label: 'Custom Layout' }
  ];

  const fontSizeOptions = [
    { value: 'small', label: 'Small' },
    { value: 'medium', label: 'Medium' },
    { value: 'large', label: 'Large' },
    { value: 'extra-large', label: 'Extra Large' }
  ];

  const refreshIntervalOptions = [
    { value: 1, label: '1 second' },
    { value: 5, label: '5 seconds' },
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 300, label: '5 minutes' }
  ];

  const handleSettingChange = (setting, value) => {
    setDisplaySettings(prev => ({
      ...prev,
      [setting]: value
    }));
  };

  const handleCheckboxChange = (setting, checked) => {
    setDisplaySettings(prev => ({
      ...prev,
      [setting]: checked
    }));
  };

  const handleResetToDefaults = () => {
    setDisplaySettings({
      currency: 'USD',
      timezone: 'America/New_York',
      dateFormat: 'MM/DD/YYYY',
      timeFormat: '12h',
      chartTheme: 'dark',
      dashboardLayout: 'grid',
      showGridLines: true,
      showVolume: true,
      showIndicators: true,
      autoRefresh: true,
      refreshInterval: 5,
      compactMode: false,
      highContrast: false,
      fontSize: 'medium',
      chartAnimations: true,
      soundAlerts: true,
      desktopNotifications: true
    });
  };

  const handleExportSettings = () => {
    const settingsData = JSON.stringify(displaySettings, null, 2);
    const blob = new Blob([settingsData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `display-preferences-${new Date()?.toISOString()?.split('T')?.[0]}.json`;
    document.body?.appendChild(a);
    a?.click();
    document.body?.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Display Preferences</h3>
          <p className="text-sm text-muted-foreground">Customize your trading interface appearance and behavior</p>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportSettings}
            iconName="Download"
            iconPosition="left"
          >
            Export Settings
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleResetToDefaults}
            iconName="RotateCcw"
            iconPosition="left"
          >
            Reset
          </Button>
        </div>
      </div>
      {/* Regional Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Regional Settings</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Primary Currency"
            description="Default currency for price display"
            options={currencyOptions}
            value={displaySettings?.currency}
            onChange={(value) => handleSettingChange('currency', value)}
          />
          <Select
            label="Timezone"
            description="Timezone for timestamps and market hours"
            options={timezoneOptions}
            value={displaySettings?.timezone}
            onChange={(value) => handleSettingChange('timezone', value)}
          />
          <Select
            label="Date Format"
            description="How dates are displayed throughout the app"
            options={dateFormatOptions}
            value={displaySettings?.dateFormat}
            onChange={(value) => handleSettingChange('dateFormat', value)}
          />
          <Select
            label="Time Format"
            description="12-hour or 24-hour time display"
            options={timeFormatOptions}
            value={displaySettings?.timeFormat}
            onChange={(value) => handleSettingChange('timeFormat', value)}
          />
        </div>
      </div>
      {/* Theme & Appearance */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Theme & Appearance</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Chart Theme"
            description="Color scheme for charts and graphs"
            options={chartThemeOptions}
            value={displaySettings?.chartTheme}
            onChange={(value) => handleSettingChange('chartTheme', value)}
          />
          <Select
            label="Dashboard Layout"
            description="How dashboard components are arranged"
            options={layoutOptions}
            value={displaySettings?.dashboardLayout}
            onChange={(value) => handleSettingChange('dashboardLayout', value)}
          />
          <Select
            label="Font Size"
            description="Text size throughout the application"
            options={fontSizeOptions}
            value={displaySettings?.fontSize}
            onChange={(value) => handleSettingChange('fontSize', value)}
          />
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Display Options</label>
            <div className="space-y-2">
              <Checkbox
                label="Compact Mode"
                description="Reduce spacing for more content"
                checked={displaySettings?.compactMode}
                onChange={(e) => handleCheckboxChange('compactMode', e?.target?.checked)}
              />
              <Checkbox
                label="High Contrast"
                description="Enhanced contrast for better visibility"
                checked={displaySettings?.highContrast}
                onChange={(e) => handleCheckboxChange('highContrast', e?.target?.checked)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Chart Settings */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Chart Display</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Chart Elements</label>
            <div className="space-y-2">
              <Checkbox
                label="Show Grid Lines"
                description="Display grid lines on charts"
                checked={displaySettings?.showGridLines}
                onChange={(e) => handleCheckboxChange('showGridLines', e?.target?.checked)}
              />
              <Checkbox
                label="Show Volume"
                description="Display volume bars below price charts"
                checked={displaySettings?.showVolume}
                onChange={(e) => handleCheckboxChange('showVolume', e?.target?.checked)}
              />
              <Checkbox
                label="Show Technical Indicators"
                description="Display technical indicators by default"
                checked={displaySettings?.showIndicators}
                onChange={(e) => handleCheckboxChange('showIndicators', e?.target?.checked)}
              />
              <Checkbox
                label="Chart Animations"
                description="Enable smooth transitions and animations"
                checked={displaySettings?.chartAnimations}
                onChange={(e) => handleCheckboxChange('chartAnimations', e?.target?.checked)}
              />
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">Auto Refresh</label>
              <Checkbox
                label="Enable Auto Refresh"
                description="Automatically update data at regular intervals"
                checked={displaySettings?.autoRefresh}
                onChange={(e) => handleCheckboxChange('autoRefresh', e?.target?.checked)}
              />
            </div>
            
            {displaySettings?.autoRefresh && (
              <Select
                label="Refresh Interval"
                description="How often to update data"
                options={refreshIntervalOptions}
                value={displaySettings?.refreshInterval}
                onChange={(value) => handleSettingChange('refreshInterval', value)}
              />
            )}
          </div>
        </div>
      </div>
      {/* Notifications & Alerts */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h4 className="text-md font-medium text-foreground mb-4">Notifications & Alerts</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Alert Types</label>
            <div className="space-y-2">
              <Checkbox
                label="Sound Alerts"
                description="Play audio notifications for alerts"
                checked={displaySettings?.soundAlerts}
                onChange={(e) => handleCheckboxChange('soundAlerts', e?.target?.checked)}
              />
              <Checkbox
                label="Desktop Notifications"
                description="Show browser notifications"
                checked={displaySettings?.desktopNotifications}
                onChange={(e) => handleCheckboxChange('desktopNotifications', e?.target?.checked)}
              />
            </div>
          </div>
          
          <div className="bg-muted rounded-md p-4">
            <div className="flex items-center space-x-2 mb-2">
              <Icon name="Info" size={16} className="text-primary" />
              <span className="text-sm font-medium text-foreground">Preview</span>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>Currency: {displaySettings?.currency}</div>
              <div>Theme: {displaySettings?.chartTheme}</div>
              <div>Layout: {displaySettings?.dashboardLayout}</div>
              <div>Font: {displaySettings?.fontSize}</div>
              <div>Refresh: {displaySettings?.autoRefresh ? `${displaySettings?.refreshInterval}s` : 'Manual'}</div>
            </div>
          </div>
        </div>
      </div>
      {/* Save Actions */}
      <div className="flex items-center justify-end space-x-4 pt-4 border-t border-border">
        <Button variant="outline" onClick={handleResetToDefaults}>
          Reset to Defaults
        </Button>
        <Button variant="default" iconName="Save" iconPosition="left">
          Save Preferences
        </Button>
      </div>
    </div>
  );
};

export default DisplayPreferences;